import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import {
    FormDatePicker,
    FormField,
    FormFieldCheckbox,
    FormFieldSelect,
} from '@/global/components/Form'
import IconeAjuda from '@/global/components/IconeAjuda'
import Grid from '@/global/components/ui/Grid'
import { Empresa } from '@/service/api/endpoints/empresas'
import { LayoutFolha } from '@acme/core'
import { useFormContext } from 'react-hook-form'
import { horariosCalculoAutomatico } from '../../constants/horarios'

export default function EmpresaPontoForm() {
    return (
        <section id="dados-ponto" className="flex gap-6 flex-col">
            <Relatorios />
            <BancoHoras />
            <IntegracaoFolha />
            <CalculoAutomatico />
            <Justificativas />
        </section>
    )
}

function Relatorios() {
    return (
        <section id="relatorios">
            <HorizontalDivider className="select-none mb-4">
                Relatórios
            </HorizontalDivider>
            <Grid container>
                <Grid item xs={12} sm={6}>
                    <FormField
                        name="diasRescisao"
                        type="number"
                        label="Dias de exibição de funcionários desligados"
                        endAdornment={
                            <IconeAjuda>
                                Essa é a quantidade de dias após a rescisão que
                                os funcionários serão listados em relatórios e
                                na tela de digitação. Após esse período, o
                                funcionário não será mais exibido e será listado
                                apenas em relatórios de competências anteriores.
                                <br />
                                <br />
                                Esse campo também controla a exibição dos
                                funcionários na tela de cadastros de centros de
                                custo.
                            </IconeAjuda>
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormFieldCheckbox
                        name="exibirSaldoDiario"
                        label="Exibir saldo diário na digitação"
                        endAdornment={
                            <IconeAjuda>
                                Caso marcado, uma coluna a mais será exibida na
                                tela de digitação de ponto, mostrando a
                                diferença do saldo diário do funcionário (extras
                                - faltas).
                            </IconeAjuda>
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormFieldCheckbox
                        name="esconderLinhasOndeNaoTrabalha"
                        label="Esconder linhas nos dias que não trabalha"
                        endAdornment={
                            <IconeAjuda>
                                <p>
                                    Quando habilitado, esconde a linha em que o
                                    funcionário não deveria trabalhar na tela de
                                    digitação (seguindo a tabela de horário e os
                                    feriados), mantendo um meio de desbloquear a
                                    linha para digitar.
                                </p>
                                <br />
                                <p>
                                    Quando desabilitado, mostra todas as linhas
                                    para edição direta.
                                </p>
                            </IconeAjuda>
                        }
                    />
                </Grid>
            </Grid>
        </section>
    )
}

function BancoHoras() {
    const { watch, setValue } = useFormContext()

    const [periodoFechamentoPonto] = watch(['periodoFechamentoPonto'])

    // campo temporário — não existe no schema, o resolver descarta no submit
    const usarBancoHoras = watch('usarBancoHoras')

    return (
        <section id="banco-horas">
            <HorizontalDivider className="select-none mb-4">
                Banco de horas
            </HorizontalDivider>
            <Grid container>
                <Grid item xs={12}>
                    <FormFieldCheckbox
                        name="usarBancoHoras"
                        label="Utilizar banco de horas"
                        defaultChecked={!!periodoFechamentoPonto}
                        onChange={marcado => {
                            if (!marcado) {
                                setValue('periodoFechamentoPonto', 0)
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        name="periodoFechamentoPonto"
                        type="number"
                        placeholder="Meses"
                        label="Período de fechamento do ponto (em meses)"
                        disabled={!usarBancoHoras}
                        endAdornment={
                            <IconeAjuda className="mb-2">
                                Período de fechamento do banco de horas
                                significa que após o término do período
                                informado, todas as horas extras e faltas dos
                                funcionarios serão zeradas, iniciando assim um
                                novo período de banco de horas.
                            </IconeAjuda>
                        }
                        required={usarBancoHoras}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormDatePicker
                        name="proximoFechamentoPonto"
                        type="month"
                        label="Competência do próximo fechamento"
                        disabled={!usarBancoHoras}
                    />
                </Grid>
            </Grid>
        </section>
    )
}

function IntegracaoFolha() {
    return (
        <section id="integracao-folha">
            <HorizontalDivider className="select-none mb-4">
                Integração com a folha de pagamento
            </HorizontalDivider>
            <Grid container>
                <Grid item xs={12} sm={5}>
                    <FormField
                        name="codEmpresaFolha"
                        label="Código da empresa"
                        endAdornment={
                            <IconeAjuda>
                                Caso use integração com folha de pagamento,
                                informe o código da empresa no seu sistema de
                                folha de pagamento.
                            </IconeAjuda>
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={7}>
                    <FormFieldSelect
                        name="layoutFolha"
                        label="Layout de exportação"
                        options={[
                            {
                                value: LayoutFolha.DOMINIO,
                                label: 'Domínio (Horas)',
                            },
                            {
                                value: LayoutFolha.DOMINIO_DECIMAL,
                                label: 'Domínio (Decimal)',
                            },
                            { value: LayoutFolha.FORTES_5, label: 'Fortes v5' },
                            { value: LayoutFolha.FORTES_6, label: 'Fortes v6' },
                            {
                                value: LayoutFolha.QUESTOR_01,
                                label: 'Questor 01',
                            },
                            {
                                value: LayoutFolha.QUESTOR_02,
                                label: 'Questor 02',
                            },
                            {
                                value: LayoutFolha.QUESTOR_03,
                                label: 'Questor 03',
                            },
                            { value: LayoutFolha.SYSTEM, label: 'System' },
                            {
                                value: LayoutFolha.DATA_CEMPRO,
                                label: 'Data Cempro',
                            },
                            { value: LayoutFolha.VIASOFT, label: 'Via Soft' },
                        ]}
                        endAdornment={
                            <IconeAjuda>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div
                                        style={{
                                            maxWidth: '50%',
                                            overflowWrap: 'break-word',
                                        }}
                                    >
                                        Essa configuração define como os dados
                                        serão exportados para a folha de
                                        pagamento.
                                    </div>
                                    <div>
                                        <span>F - Funcionário</span>
                                        <br />
                                        <span>C - Competência</span>
                                        <br />
                                        <span>R - Rubrica</span>
                                        <br />
                                        <span>V - Valor</span>
                                        <br />
                                        <span>E - Empresa</span>
                                        <br />
                                        <span>P - Pis</span>
                                        <br />
                                        <span>H - Horas</span>
                                        <br />
                                        <span>M - Minutos</span>
                                    </div>
                                </div>
                                <br />
                                <br />
                                <div
                                    style={{
                                        overflowWrap: 'break-word',
                                        overflowY: 'auto',
                                        maxHeight: '150px',
                                    }}
                                >
                                    <li>
                                        <strong>Domínio</strong>:<br />
                                        10FFFFFFFFFFCCCCCCRRRR11VVVVVVVVVEEEEEEEEEE
                                    </li>
                                    <li>
                                        <strong>Fortes</strong>:
                                        <br />
                                        1FFFFFFRRRvvvvvvv.vvVVVVVVVVVVVV.VV
                                    </li>
                                    <li>
                                        <strong>Questor 01</strong>:
                                        <br />
                                        0;EEEE;000;FFFFFFFFF;RRRR;0000;vvvvvvvvvvv;VVVVVVVVVVV;
                                    </li>
                                    <li>
                                        <strong>Questor 02</strong>:
                                        <br />
                                        EEE;FFFFFF;RRR;VVVVV;vvvvvvvvv;
                                    </li>
                                    <li>
                                        <strong>Questor 03</strong>:
                                        <br />
                                        EEEE;FFFFFF;RRR;VVV,VV
                                    </li>
                                    <li>
                                        <strong>System</strong>:
                                        <br />
                                        RRRRFFFFFFvvvv.vvVVVVVVVVVVVV.VV
                                    </li>
                                    <li>
                                        <strong>Data Cempro</strong>:
                                        <br />
                                        FFFFFFFFFFRRRRVVV:VV:VVPPPPPPPPPPP
                                    </li>
                                    <li>
                                        <strong>Via Soft</strong>:
                                        <br />
                                        FFFFFFFFFFFRRRRRHHHMM
                                    </li>
                                </div>
                            </IconeAjuda>
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormFieldCheckbox
                        name="exportarHorasFaltaJustificada"
                        label="Exportar faltas justificadas em horas"
                        endAdornment={
                            <IconeAjuda>
                                Caso esteja marcado, as faltas justificadas e
                                injustificadas serão exportadas em horas, caso
                                contrário, serão exportadas em dias. Quando
                                banco de horas está habilitado, apenas as faltas
                                da competência atual serão exportadas em dias.
                            </IconeAjuda>
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={5}>
                    <FormFieldCheckbox
                        name="exportarHorasFaltaIntegral"
                        label="Exportar faltas integrais em horas"
                        endAdornment={
                            <IconeAjuda>
                                Caso esteja marcado, as faltas integrais serão
                                exportadas em horas, caso contrário, serão
                                exportadas em dias. Não é compatível com banco
                                de horas.
                            </IconeAjuda>
                        }
                    />
                </Grid>
            </Grid>
        </section>
    )
}

function CalculoAutomatico() {
    const { watch, setValue } = useFormContext()

    // campo só de tela — vem do `defaultValues` como `!!hrCalculoAutomatico` e
    // o schema do formulário tira do payload antes de enviar
    const calculoAutomatico = watch('calculoAutomatico')

    return (
        <section id="calculo-automatico">
            <HorizontalDivider className="select-none mb-4">
                <span className="inline-flex items-center">
                    Cálculo automático&nbsp;
                    <IconeAjuda>
                        O cálculo automático é realizado uma vez ao dia, no
                        horário selecionado. Ele é responsável por gerar os
                        espelhos de ponto e calcular as jornadas de trabalho de
                        todos os funcionários da empresa.
                        <br />
                        <br />
                        Se o cálculo automático estiver desabilitado, você
                        precisará realizar o cálculo manualmente através do
                        botão "Calcular ponto" na tela de digitação ou de
                        cálculo de ponto sempre que desejar atualizar os
                        espelhos e saldos dos funcionários.
                    </IconeAjuda>
                </span>
            </HorizontalDivider>
            <Grid container>
                <Grid item xs={12} sm={6}>
                    <FormFieldCheckbox
                        name="calculoAutomatico"
                        label="Habilitar cálculo automático"
                        onChange={marcado => {
                            if (!marcado) setValue('hrCalculoAutomatico', null)
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    {/* `defaultValue` explícito: `parseDataTransform` apaga
                        a chave quando a empresa não tem hora, e aí o `''`
                        padrão do select estoura o `@Length(5, 5)` do DTO */}
                    <FormFieldSelect
                        name="hrCalculoAutomatico"
                        label="Horário do cálculo automático"
                        placeholder="Selecione uma opção"
                        defaultValue={null}
                        disabled={!calculoAutomatico}
                        required={calculoAutomatico}
                        options={horariosCalculoAutomatico.map(horario => ({
                            value: horario,
                            label: horario,
                        }))}
                    />
                </Grid>
            </Grid>
        </section>
    )
}

function Justificativas() {
    const { watch, setValue } = useFormContext<Empresa>()
    const justificativa = watch('justificativaPadraoRegistro')
    const exigirJustificativa = justificativa == null

    return (
        <section id="justificativas">
            <HorizontalDivider className="select-none mb-4">
                Justificativas
            </HorizontalDivider>
            <Grid container>
                <Grid item xs={12}>
                    <FormFieldCheckbox
                        name="exigirJustificativa"
                        label="Exigir justificativa ao incluir registros"
                        defaultChecked={exigirJustificativa}
                        onChange={marcado =>
                            setValue(
                                'justificativaPadraoRegistro',
                                marcado ? null : 'Inserido manualmente',
                            )
                        }
                        endAdornment={
                            <IconeAjuda>
                                <p>
                                    Quando habilitado, sempre que um registro
                                    manual for inserido na digitação, uma
                                    justificativa será exigida para a inserção
                                    do registro.
                                </p>
                                <br />
                                <p>
                                    Quando desabilitado, a justificativa padrão
                                    será utilizada.
                                </p>
                            </IconeAjuda>
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        name="justificativaPadraoRegistro"
                        label="Justificativa padrão"
                        multiline
                        disabled={exigirJustificativa}
                        max={190}
                    />
                </Grid>
            </Grid>
        </section>
    )
}
